import {
    type CSSProperties,
    type ReactNode,
    useEffect,
    useRef
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    NCoreUIKitTheme,
    PaletteSwitcher
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Caption,
    Preview,
    Section
} from "../../components/content";

const NAME = "PaletteSwitcher";

const USAGE_CODE = `<PaletteSwitcher />

<PaletteSwitcher
    form="segment"
/>`;

const VARIANTS_CODE = `<PaletteSwitcher
    variants={[
        {
            iconColor: colors.content.icon.info,
            paletteKey: "nibgat",
            title: "Ocean"
        },
        {
            iconColor: colors.content.icon.danger,
            paletteKey: "caycore",
            title: "Rose"
        }
    ]}
    isShowTitle={true}
/>`;

const FORMS = [
    "cycle",
    "menu",
    "segment"
] as const;

const DEMO_ITEM_STYLE: CSSProperties = {
    flexDirection: "column",
    alignItems: "center",
    display: "flex"
};

const DEMO_CONTENT_STYLE: CSSProperties = {
    justifyContent: "center",
    display: "flex",
    width: "100%"
};

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("paletteSwitcherPage.formsTitle"),
        id: "forms"
    },
    {
        title: t("paletteSwitcherPage.displayTitle"),
        id: "display"
    },
    {
        title: t("paletteSwitcherPage.variantsTitle"),
        id: "variants"
    },
    {
        title: t("paletteSwitcherPage.styleTitle"),
        id: "style"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const DemoItem = ({
    children,
    label
}: {
    children: ReactNode;
    label: string;
}) => <div
    style={DEMO_ITEM_STYLE}
>
    <div
        style={DEMO_CONTENT_STYLE}
    >
        {children}
    </div>
    <Caption>
        {label}
    </Caption>
</div>;

const PaletteSwitcherPage = () => {
    const {
        t
    } = useDocsI18n();

    const {
        activePalette,
        colors
    } = NCoreUIKitTheme.useContext();

    const initialPaletteRef = useRef(activePalette);

    useEffect(() => {
        return () => {
            NCoreUIKitTheme.switch({
                paletteKey: initialPaletteRef.current
            });
        };
    }, []);

    return <Article>
        <ComponentHeader
            lead={t("paletteSwitcherPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("paletteSwitcherPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("paletteSwitcherPage.overviewCaption")}
            >
                <PaletteSwitcher/>
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("paletteSwitcherPage.formsTitle")}
            id="forms"
        >
            <Paragraph>
                {t("paletteSwitcherPage.formsText")}
            </Paragraph>
            <Preview>
                {
                    FORMS.map((form) => <DemoItem
                        label={form}
                        key={form}
                    >
                        <PaletteSwitcher
                            form={form}
                        />
                    </DemoItem>)
                }
            </Preview>
        </Section>
        <Section
            title={t("paletteSwitcherPage.displayTitle")}
            id="display"
        >
            <Paragraph>
                {t("paletteSwitcherPage.displayText")}
            </Paragraph>
            <Preview>
                <DemoItem
                    label="isShowTitle"
                >
                    <PaletteSwitcher
                        isShowTitle={true}
                    />
                </DemoItem>
                <DemoItem
                    label="title"
                >
                    <PaletteSwitcher
                        title={t("paletteSwitcherPage.customTitle")}
                    />
                </DemoItem>
                <DemoItem
                    label="isShowNextVariant"
                >
                    <PaletteSwitcher
                        isShowNextVariant={true}
                        isShowTitle={true}
                    />
                </DemoItem>
            </Preview>
        </Section>
        <Section
            title={t("paletteSwitcherPage.variantsTitle")}
            id="variants"
        >
            <Paragraph>
                {t("paletteSwitcherPage.variantsText")}
            </Paragraph>
            <Preview>
                <PaletteSwitcher
                    variants={[
                        {
                            title: t("paletteSwitcherPage.ocean"),
                            iconColor: colors.content.icon.info,
                            paletteKey: "nibgat"
                        },
                        {
                            iconColor: colors.content.icon.danger,
                            title: t("paletteSwitcherPage.rose"),
                            paletteKey: "caycore"
                        }
                    ]}
                    isShowTitle={true}
                />
            </Preview>
            <CodeBlock
                code={VARIANTS_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("paletteSwitcherPage.styleTitle")}
            id="style"
        >
            <Paragraph>
                {t("paletteSwitcherPage.styleText")}
            </Paragraph>
            <Preview>
                <DemoItem
                    label="variant=outline"
                >
                    <PaletteSwitcher
                        variant="outline"
                    />
                </DemoItem>
                <DemoItem
                    label="variant=ghost"
                >
                    <PaletteSwitcher
                        variant="ghost"
                    />
                </DemoItem>
                <DemoItem
                    label="type=primary"
                >
                    <PaletteSwitcher
                        type="primary"
                    />
                </DemoItem>
                <DemoItem
                    label="color=raised"
                >
                    <PaletteSwitcher
                        variant="outline"
                        color="raised"
                    />
                </DemoItem>
                <DemoItem
                    label="color=subtle"
                >
                    <PaletteSwitcher
                        variant="outline"
                        color="subtle"
                    />
                </DemoItem>
                <DemoItem
                    label="color=transparent"
                >
                    <PaletteSwitcher
                        color="transparent"
                        variant="outline"
                    />
                </DemoItem>
                <DemoItem
                    label="size=large"
                >
                    <PaletteSwitcher
                        size="large"
                    />
                </DemoItem>
                <DemoItem
                    label="customTheme=light"
                >
                    <PaletteSwitcher
                        customTheme={{
                            themeKey: "light"
                        }}
                    />
                </DemoItem>
                <DemoItem
                    label="customTheme=dark"
                >
                    <PaletteSwitcher
                        customTheme={{
                            themeKey: "dark"
                        }}
                    />
                </DemoItem>
                <DemoItem
                    label="isDisabled"
                >
                    <PaletteSwitcher
                        isDisabled={true}
                    />
                </DemoItem>
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("paletteSwitcherPage.a11yName"),
                    t("paletteSwitcherPage.a11yDot"),
                    t("paletteSwitcherPage.a11yLabelInName"),
                    t("paletteSwitcherPage.a11yMenu"),
                    t("paletteSwitcherPage.a11ySegment"),
                    t("paletteSwitcherPage.a11yDisabled"),
                    t("paletteSwitcherPage.a11yCustom"),
                    t("paletteSwitcherPage.a11yMotion")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("paletteSwitcherPage.propForm"),
                        type: "\"cycle\" | \"menu\" | \"segment\"",
                        defaultValue: "\"cycle\"",
                        name: "form"
                    },
                    {
                        description: t("paletteSwitcherPage.propIsShowTitle"),
                        name: "isShowTitle",
                        type: "boolean"
                    },
                    {
                        description: t("paletteSwitcherPage.propTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("paletteSwitcherPage.propIsShowNextVariant"),
                        name: "isShowNextVariant",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        type: "Array<{ paletteKey, title, icon, iconColor, backgroundColor, borderColor }>",
                        description: t("paletteSwitcherPage.propVariants"),
                        name: "variants"
                    },
                    {
                        type: "\"filled\" | \"tonal\" | \"outline\" | \"ghost\"",
                        description: t("paletteSwitcherPage.propVariant"),
                        defaultValue: "\"tonal\"",
                        name: "variant"
                    },
                    {
                        type: "\"primary\" | \"danger\" | \"warning\" | \"neutral\" | \"success\" | \"info\"",
                        description: t("paletteSwitcherPage.propType"),
                        defaultValue: "\"neutral\"",
                        name: "type"
                    },
                    {
                        type: "\"xSmall\" | \"small\" | \"medium\" | \"large\"",
                        description: t("paletteSwitcherPage.propSize"),
                        defaultValue: "\"medium\"",
                        name: "size"
                    },
                    {
                        description: t("paletteSwitcherPage.propSpreadBehaviour"),
                        type: "\"baseline\" | \"stretch\" | \"free\"",
                        defaultValue: "\"baseline\"",
                        name: "spreadBehaviour"
                    },
                    {
                        description: t("paletteSwitcherPage.propIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        type: "keyof ContainerContentColors | \"transparent\"",
                        description: t("paletteSwitcherPage.propColor"),
                        name: "color"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("paletteSwitcherPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("paletteSwitcherPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    },
                    {
                        description: t("paletteSwitcherPage.propAccessibilityLabel"),
                        name: "accessibilityLabel",
                        type: "string"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("paletteSwitcherPage.propsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default PaletteSwitcherPage;
